/**
 * TutorialDirector: the guided first run's step machine (docs/design/tutorial.md). Pure (no DOM,
 * no sim calls): it reads the world and the sim events each tick and decides
 *  - which step is active, and advances only when the sim shows the thing happened (a cast
 *    landed, a proc was consumed, a telegraph was escaped, a kick landed), never on timers;
 *  - which action-bar binds (and Dodge) are unlocked, queueing a "New skill" toast per unlock;
 *  - what the bots may do (hold at the gate, only the tank, everyone; kicks held back while the
 *    player is learning to kick or dispel);
 *  - the prompt the overlay shows (title, one line with [key] chips, the button / frame to point
 *    at, the ground marker) plus small running stats for the graduation summary.
 * The scripts (what each class learns, in which order) live in scripts.ts.
 */
import type { SimEvent, Vec2 } from '@keystone/sim';
import type { UiBind } from '../input/bindings';

// ---------------------------------------------------------------------------------------------
// The slice of the world the director reads (the real World satisfies it structurally).
// ---------------------------------------------------------------------------------------------

export interface TutAura {
  readonly auraId: string;
  readonly stacks: number;
}
export interface TutUnit {
  readonly id: string;
  readonly name: string;
  readonly alive: boolean;
  readonly pos: Readonly<Vec2>;
  readonly inCombat: boolean;
  readonly targetId: string | null;
  readonly npcId: string | null;
  readonly role: string | null;
  readonly hp: number;
  readonly maxHp: number;
  readonly resources: Readonly<Partial<Record<string, Readonly<{ cur: number; max: number }>>>>;
  readonly auras: readonly TutAura[];
  readonly cast: Readonly<{ abilityId: string; interruptible: boolean }> | null;
}
export interface TutWorld {
  readonly time: number;
  readonly units: Readonly<Record<string, TutUnit>>;
  readonly partyIds: readonly string[];
  readonly packs: Readonly<Record<string, Readonly<{ memberIds: readonly string[] }>>>;
}

/** Ids the director resolves from the world once (tutorial dungeon pack ids). */
export interface TutLayout {
  packs: { dummy: string; golem: string; lesson: string; graduation: string };
  moveSpot: Vec2;
}

/** Content questions, injected so tests need no registry. */
export interface TutContentQueries {
  /** The ability is an interrupt (bots hold these during the kick / dispel lessons). */
  isKick(abilityId: string): boolean;
  /** The aura is a debuff the player's dispel can remove (poison / magic). */
  isDispellable(auraId: string): boolean;
  /** The world shape (telegraph area) contains the point (unit radius). */
  contains(area: unknown, p: Vec2, radius: number): boolean;
}

export interface TutFacts {
  now: number;
  world: TutWorld;
  me: TutUnit | undefined;
  /** Client-side selection (falls back to the sim's targetId). */
  selectedId: string | null;
  tank: TutUnit | undefined;
  dummy: TutUnit | undefined;
  golem: TutUnit | undefined;
  lesson: TutUnit[];
  graduation: TutUnit[];
  party: TutUnit[];
  /** Golem alive and fighting. */
  golemEngaged: boolean;
  /** An orange telegraph covers the player right now. */
  threatened: boolean;
}

export const unitOf = (w: TutWorld, id: string | null | undefined): TutUnit | undefined => (id ? w.units[id] : undefined);
export const hasAura = (u: TutUnit | undefined, auraId: string): boolean => !!u?.auras.some((a) => a.auraId === auraId);
export const auraStacks = (u: TutUnit | undefined, auraId: string): number => u?.auras.find((a) => a.auraId === auraId)?.stacks ?? 0;
export const res = (u: TutUnit | undefined, id: string): number => u?.resources[id]?.cur ?? 0;
export const allDead = (us: readonly TutUnit[]): boolean => us.length > 0 && us.every((u) => !u.alive);
export const anyEngaged = (us: readonly TutUnit[]): boolean => us.some((u) => u.alive && u.inCombat);
export const dist = (a: Readonly<Vec2>, b: Readonly<Vec2>): number => Math.hypot(a.x - b.x, a.y - b.y);

// ---------------------------------------------------------------------------------------------
// Scripts
// ---------------------------------------------------------------------------------------------

export type Lock = UiBind | 'dodge';
export type BotMode = 'hold' | 'tank' | 'free';
export type Focus = Lock | 'party' | null;
export type MarkerKind = 'spot' | 'dummy' | 'golem' | 'lesson' | 'graduation' | 'tank';

export interface Unlock {
  lock: Lock;
  abilityId: string;
  name: string;
  /** Keycap label ("1", "Q", "Shift"). */
  key: string;
  /** One line: what it's for. */
  why: string;
}

/** Step-local scratch: counters and flags filled by the director's event bookkeeping and `on`. */
export interface StepState {
  enteredAt: number;
  n: Record<string, number>;
  flag: Record<string, boolean>;
}

export interface Goal {
  label: string;
  done(s: StepState, f: TutFacts): boolean;
}

export interface StepDef {
  id: string;
  title: string;
  /** One line; `[X]` renders as a keycap chip. */
  text: string;
  /** Replaces `text` while it returns a string (e.g. "Riposte is lit: press [6] now!"). */
  hint?(s: StepState, f: TutFacts): string | null;
  unlock?: Unlock[];
  /** The button / frame the overlay points at. */
  focus?: Focus | ((s: StepState, f: TutFacts) => Focus);
  marker?: MarkerKind | ((s: StepState, f: TutFacts) => MarkerKind | null);
  bots?: BotMode | ((f: TutFacts, s: StepState) => BotMode);
  /** Bots don't interrupt while this is true (the player learns to kick / dispel). */
  holdKicks?(s: StepState, f: TutFacts): boolean;
  /** Checklist under the prompt (informational). */
  goals?: Goal[];
  /** The graduation pull: the rotation score samples while this step is active. */
  scored?: boolean;
  enter?(s: StepState, f: TutFacts): void;
  on?(e: SimEvent, s: StepState, f: TutFacts): void;
  done(s: StepState, f: TutFacts): boolean;
  /** Give up on this step (its goal became impossible); unlocks still apply. */
  abortIf?(f: TutFacts): boolean;
}

export interface TutorialScript {
  classId: string;
  role: 'tank' | 'healer' | 'melee' | 'ranged';
  steps: StepDef[];
  /** Jump straight to a step when something happens early (e.g. the lesson pack got pulled). */
  jumps?: { to: string; when(f: TutFacts): boolean }[];
  /** Every bind the class has (unlocked at the end at the latest). */
  allLocks: Lock[];
}

// ---------------------------------------------------------------------------------------------
// Overlay view model
// ---------------------------------------------------------------------------------------------

export interface PromptView {
  stepNo: number;
  stepCount: number;
  title: string;
  text: string;
  focus: Focus;
  marker: Vec2 | null;
  goals: { label: string; done: boolean }[];
  /** Urgent one-liner over everything (an orange telegraph is on you). */
  alert: string | null;
}

export interface Toast {
  id: number;
  unlock: Unlock;
}

export interface TutorialStats {
  kicks: number;
  dispels: number;
  dodges: number;
  telegraphHits: number;
  procsUsed: number;
}

interface TeleTrack {
  /** The player stood in it when it appeared. */
  inside: boolean;
  area: unknown;
}

export interface DirectorOptions {
  playerId: string;
  layout: TutLayout;
  queries: TutContentQueries;
}

export class TutorialDirector {
  private idx = 0;
  private s: StepState = { enteredAt: 0, n: {}, flag: {} };
  private started = false;
  private readonly unlocked = new Set<Lock>();
  private toastSeq = 0;
  private readonly toasts: Toast[] = [];
  private readonly tele = new Map<string, TeleTrack>();
  private facts: TutFacts | null = null;
  readonly stats: TutorialStats = { kicks: 0, dispels: 0, dodges: 0, telegraphHits: 0, procsUsed: 0 };
  /** Set once the last step is done. */
  finished = false;

  constructor(
    readonly script: TutorialScript,
    private readonly opts: DirectorOptions,
  ) {}

  get stepIndex(): number {
    return this.idx;
  }
  get step(): StepDef | null {
    return this.script.steps[this.idx] ?? null;
  }
  get state(): Readonly<StepState> {
    return this.s;
  }

  isUnlocked(l: Lock): boolean {
    return this.finished || this.unlocked.has(l);
  }

  unlockedLocks(): Lock[] {
    return this.finished ? [...this.script.allLocks, 'dodge'] : [...this.unlocked];
  }

  /** Toasts queued since the last call. */
  drainToasts(): Toast[] {
    return this.toasts.splice(0);
  }

  /** The bot mode right now (hold / only the tank / everyone). */
  botMode(): BotMode {
    if (this.finished) return 'free';
    const st = this.step;
    const f = this.facts;
    if (!st || !f) return 'hold';
    const b = st.bots ?? 'hold';
    return typeof b === 'function' ? b(f, this.s) : b;
  }

  /** Should this bot's command go through? */
  allowBotCommand(botId: string, cmd: { type: string; abilityId?: string }): boolean {
    const mode = this.botMode();
    const f = this.facts;
    if (mode === 'hold') return false;
    if (mode === 'tank' && f?.tank?.id !== botId) return false;
    if (cmd.type === 'cast' && cmd.abilityId && this.opts.queries.isKick(cmd.abilityId)) {
      const st = this.step;
      if (!this.finished && st?.holdKicks && f && st.holdKicks(this.s, f)) return false;
    }
    return true;
  }

  /** The graduation pull is on (rotation score samples). */
  scoring(): boolean {
    return !this.finished && !!this.step?.scored;
  }

  /** One sim tick: bookkeeping, step checks, advancing. */
  update(events: readonly SimEvent[], world: TutWorld, selectedId: string | null): void {
    const f = (this.facts = this.factsOf(world, selectedId));
    if (!this.started) {
      this.started = true;
      this.enter(0, f);
    }
    for (const e of events) this.track(e, f);
    f.threatened = this.threatened(f);
    if (this.finished) return;
    // Early jumps (the lesson pack got pulled before its lesson, ...).
    for (const j of this.script.jumps ?? []) {
      const to = this.script.steps.findIndex((x) => x.id === j.to);
      if (to > this.idx && j.when(f)) {
        this.advanceTo(to, f);
        break;
      }
    }
    // Advance through every step whose goal is met (or became impossible) this tick.
    for (let guard = 0; guard < this.script.steps.length && !this.finished; guard++) {
      const st = this.step!;
      if (!(st.done(this.s, f) || st.abortIf?.(f))) break;
      this.advanceTo(this.idx + 1, f);
    }
  }

  /** The overlay's view model for the current state. */
  view(): PromptView | null {
    const st = this.step;
    const f = this.facts;
    if (this.finished || !st || !f) return null;
    const hint = st.hint?.(this.s, f) ?? null;
    const focus = typeof st.focus === 'function' ? st.focus(this.s, f) : (st.focus ?? null);
    const mk = typeof st.marker === 'function' ? st.marker(this.s, f) : (st.marker ?? null);
    return {
      stepNo: this.idx + 1,
      stepCount: this.script.steps.length,
      title: st.title,
      text: hint ?? st.text,
      focus,
      marker: mk ? this.markerPos(mk, f) : null,
      goals: (st.goals ?? []).map((g) => ({ label: g.label, done: g.done(this.s, f) })),
      alert: f.threatened && f.me?.alive ? (this.isUnlocked('dodge') ? 'Orange on you! Press [Shift] to dash out' : 'Orange on you! Walk out of it') : null,
    };
  }

  // -------------------------------------------------------------------------------------------

  private advanceTo(i: number, f: TutFacts): void {
    // Unlocks of steps jumped over still apply.
    for (let k = this.idx + 1; k < i && k < this.script.steps.length; k++) this.grant(this.script.steps[k]!.unlock, false);
    if (i >= this.script.steps.length) {
      this.idx = this.script.steps.length;
      this.finished = true;
      for (const l of this.script.allLocks) this.unlocked.add(l);
      this.unlocked.add('dodge');
      return;
    }
    this.enter(i, f);
  }

  private enter(i: number, f: TutFacts): void {
    this.idx = i;
    this.s = { enteredAt: f.now, n: {}, flag: {} };
    const st = this.script.steps[i]!;
    this.grant(st.unlock, true);
    st.enter?.(this.s, f);
  }

  private grant(list: Unlock[] | undefined, toast: boolean): void {
    for (const u of list ?? []) {
      if (this.unlocked.has(u.lock)) continue;
      this.unlocked.add(u.lock);
      if (toast) this.toasts.push({ id: ++this.toastSeq, unlock: u });
    }
  }

  /** Common event bookkeeping into the step state (cast:<id>, gain:<aura>, kick, dispel...). */
  private track(e: SimEvent, f: TutFacts): void {
    const me = this.opts.playerId;
    const s = this.s;
    const inc = (k: string): void => {
      s.n[k] = (s.n[k] ?? 0) + 1;
    };
    switch (e.type) {
      case 'cast':
        if (e.sourceID === me) inc(`cast:${e.abilityGameID}`);
        break;
      case 'applybuff':
      case 'applybuffstack':
      case 'refreshbuff':
        if (e.targetID === me) inc(`gain:${e.abilityGameID}`);
        break;
      case 'removebuff':
        if (e.targetID === me && e.reason === 'consumed') {
          inc(`consumed:${e.abilityGameID}`);
          this.stats.procsUsed++;
        }
        break;
      case 'interrupt':
        if (e.sourceID === me) {
          inc('kick');
          this.stats.kicks++;
        }
        break;
      case 'dispel':
        if (e.sourceID === me && !e.isBuff) {
          inc('dispel');
          this.stats.dispels++;
        }
        break;
      case 'heal':
        if (e.sourceID === me && !e.tick) inc(`heal:${e.targetID}:${e.abilityGameID}`);
        break;
      case 'damage':
        if (e.targetID === me && e.sourceID === f.golem?.id) inc('golemHit');
        break;
      case 'telegraph_start': {
        const u = f.me;
        const inside = !!u?.alive && this.opts.queries.contains(e.area, u.pos, 0.5);
        this.tele.set(e.telegraphId, { inside, area: e.area });
        break;
      }
      case 'telegraph_end': {
        const t = this.tele.get(e.telegraphId);
        this.tele.delete(e.telegraphId);
        const hit = e.hitIds.includes(me);
        if (hit) {
          inc('teleHit');
          this.stats.telegraphHits++;
        } else if (t?.inside) {
          inc('dodged');
          this.stats.dodges++;
        }
        break;
      }
      default:
        break;
    }
    if (!this.finished) this.step?.on?.(e, s, f);
  }

  private factsOf(world: TutWorld, selectedId: string | null): TutFacts {
    const L = this.opts.layout;
    const members = (packId: string): TutUnit[] => (world.packs[packId]?.memberIds ?? []).map((id) => world.units[id]).filter((u): u is TutUnit => !!u);
    const me = world.units[this.opts.playerId];
    const party = world.partyIds.map((id) => world.units[id]).filter((u): u is TutUnit => !!u);
    const golem = members(L.packs.golem)[0];
    const f: TutFacts = {
      now: world.time,
      world,
      me,
      selectedId: selectedId ?? me?.targetId ?? null,
      tank: party.find((u) => u.role === 'tank'),
      dummy: members(L.packs.dummy)[0],
      golem,
      lesson: members(L.packs.lesson),
      graduation: members(L.packs.graduation),
      party,
      golemEngaged: !!golem?.alive && golem.inCombat,
      threatened: false,
    };
    f.threatened = this.threatened(f);
    return f;
  }

  /** A pending telegraph covers the player right now. */
  private threatened(f: TutFacts): boolean {
    const me = f.me;
    if (!me?.alive) return false;
    for (const t of this.tele.values()) if (this.opts.queries.contains(t.area, me.pos, 0.5)) return true;
    return false;
  }

  private markerPos(k: MarkerKind, f: TutFacts): Vec2 | null {
    const centroid = (us: TutUnit[]): Vec2 | null => {
      const alive = us.filter((u) => u.alive);
      if (!alive.length) return null;
      return { x: alive.reduce((a, u) => a + u.pos.x, 0) / alive.length, y: alive.reduce((a, u) => a + u.pos.y, 0) / alive.length };
    };
    switch (k) {
      case 'spot':
        return this.opts.layout.moveSpot;
      case 'dummy':
        return f.dummy?.alive ? f.dummy.pos : null;
      case 'golem':
        return f.golem?.alive ? f.golem.pos : null;
      case 'lesson':
        return centroid(f.lesson);
      case 'graduation':
        return centroid(f.graduation);
      case 'tank':
        return f.tank && f.tank.id !== f.me?.id ? f.tank.pos : null;
    }
  }
}
